/**
 * Protected Route component for authentication guard
 */

import React from 'react'
import { Navigate, useLocation } from 'react-router'
import { useAuth } from '../../context/auth'
import { Spinner } from '@/design-system/atoms/Spinner'

interface ProtectedRouteProps {
  children: React.ReactNode
  requirePermission?: string
  requireSuperuser?: boolean
}

export function ProtectedRoute({
  children,
  requirePermission,
  requireSuperuser = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user, permissions } = useAuth()
  const location = useLocation()

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="xl" className="text-primary-500" />
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />
  }

  // Check superuser requirement
  if (requireSuperuser && !user?.is_superuser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">
            Acesso Negado
          </h1>
          <p className="text-gray-600">
            Você não tem permissão para acessar esta página.
          </p>
        </div>
      </div>
    )
  }

  // Check specific permission requirement
  if (requirePermission && permissions) {
    const hasPermission =
      permissions.allowedAgents?.includes(requirePermission) ||
      (requirePermission === 'documents' && permissions.canManageDocuments) ||
      (requirePermission === 'users' && permissions.canManageUsers)

    if (!hasPermission) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">
              Acesso Negado
            </h1>
            <p className="text-gray-600">
              Você não tem permissão para acessar este recurso (
              {requirePermission}).
            </p>
          </div>
        </div>
      )
    }
  }

  return <>{children}</>
}

export default ProtectedRoute
